import { useEffect, useState } from "react";
import { Loader2, LogOut, FileDown, User, Scale, ClipboardList, Landmark, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cliente, crmError, clearClienteSession } from "@/lib/crm";
import { ClienteChat } from "@/components/area/ClienteChat";
import { ClienteDocumenti, ClienteProiezione } from "@/components/area/ClienteDocumenti";

const humanize = (s) => (s ? String(s).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : null);

const Card = ({ title, icon: Icon, children, testId }) => (
  <section data-testid={testId} className="rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgba(30,58,95,0.08)]">
    <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-zr-blue md:text-lg">
      {Icon && <Icon className="h-4 w-4 text-zr-green" />} {title}
    </h2>
    {children}
  </section>
);

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[#ECEFF1] py-2 text-sm last:border-0">
    <span className="text-zr-grey">{label}</span>
    <span className="text-right font-medium text-zr-blue">{value || "—"}</span>
  </div>
);

const CopyValue = ({ label, value, testId }) => {
  const copy = () => navigator.clipboard?.writeText(String(value)).then(() => toast.success(`${label} copiato`)).catch(() => {});
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#ECEFF1] py-2 text-sm last:border-0">
      <span className="text-zr-grey">{label}</span>
      <span className="flex items-center gap-2">
        <span data-testid={testId} className="text-right font-semibold tracking-wide text-zr-blue">{value || "—"}</span>
        {value && <button type="button" onClick={copy} aria-label={`Copia ${label}`} data-testid={`${testId}-copy`} className="rounded-lg bg-zr-light p-1.5 text-zr-blue hover:bg-[#D6EBFB]"><Copy className="h-3.5 w-3.5" /></button>}
      </span>
    </div>
  );
};

function PagamentoBonifico({ p }) {
  if (!p) return null;
  return (
    <Card title="Istruzioni di pagamento" icon={Landmark} testId="card-pagamento">
      {p.gia_pagato ? (
        <p data-testid="pagamento-saldato" className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#E8F5E9] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#2E7D32]">
          <CheckCircle2 className="h-3.5 w-3.5" /> Già saldato
        </p>
      ) : (
        <p data-testid="pagamento-da-saldare" className="mb-3 inline-flex rounded-full bg-zr-light px-3 py-1 text-xs font-bold uppercase tracking-wide text-zr-blue">Da saldare</p>
      )}
      <Row label="Intestatario" value={p.intestatario} />
      <CopyValue label="IBAN" value={p.iban} testId="pagamento-iban" />
      <CopyValue label="Causale" value={p.causale} testId="pagamento-causale" />
      <Row label="Importo" value={p.importo_eur != null ? eur(p.importo_eur) : null} />
      {p.nota && <p data-testid="pagamento-nota" className="mt-3 rounded-xl bg-zr-bg px-3 py-2 text-xs leading-relaxed text-zr-grey">{p.nota}</p>}
      {!p.gia_pagato && <p className="mt-3 text-xs text-zr-grey">Dopo il bonifico carica la ricevuta nel riquadro "Copia bonifico" qui a fianco.</p>}
    </Card>
  );
}

function Timeline({ items }) {
  if (!items?.length) return <p className="text-sm text-zr-grey">Nessuna fase registrata al momento.</p>;
  return (
    <ol className="space-y-3">
      {items.map((t, i) => (
        <li key={i} data-testid={`timeline-item-${i}`} className="flex gap-3 text-sm">
          <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-zr-green" />
          <div>
            <p className="font-semibold text-zr-blue">{t.descrizione || t.titolo || t.label || humanize(t.stato) || "Fase"}</p>
            {t.descrizione && t.stato && <p className="text-xs text-zr-grey">{humanize(t.stato)}</p>}
            {fmtDate(t.data || t.created_at || t.timestamp) && <p className="text-xs text-[#90A4AE]">{fmtDate(t.data || t.created_at || t.timestamp)}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export const ClienteDashboard = ({ onLogout }) => {
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    cliente
      .me()
      .then(setMe)
      .catch((e) => {
        if (e?.response?.status === 401) {
          clearClienteSession();
          onLogout();
          return;
        }
        setErr(crmError(e));
      });
  }, [onLogout]);

  const downloadMiniMandato = async () => {
    setDownloading(true);
    try {
      const r = await cliente.miniMandato();
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ZERAMI_Mini-incarico.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "Mini-incarico non ancora disponibile per questa pratica";
      try {
        const txt = await e?.response?.data?.text?.();
        if (txt) msg = JSON.parse(txt).detail || msg;
      } catch (_) {}
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const nome = [me?.lead_nome, me?.lead_cognome].filter(Boolean).join(" ");

  return (
    <main data-testid="area-cliente-dashboard" className="min-h-screen bg-zr-bg font-inter text-zr-blue">
      <header className="border-b border-[#E0E0E0] bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 lg:px-8">
          <a href="/" aria-label="ZERAMI"><img src="/logo-zerami.png" alt="ZERAMI" className="h-12 w-auto object-contain" /></a>
          <div className="flex items-center gap-4">
            {nome && <span data-testid="cliente-nome" className="hidden text-sm font-semibold text-zr-blue sm:block">{nome}</span>}
            <button onClick={onLogout} data-testid="cliente-logout" className="inline-flex items-center gap-1.5 rounded-xl bg-zr-light px-3 py-2 text-xs font-semibold text-zr-blue transition-colors hover:bg-[#D6EBFB]">
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight text-zr-blue sm:text-4xl">La tua pratica</h1>
        <p className="mt-1 text-sm text-zr-grey md:text-base">Stato, fasi, documenti e chat con il tuo advisor.</p>

        {err && <p data-testid="dashboard-error" className="mt-6 rounded-xl border border-[#EF9A9A] bg-[#FFEBEE] px-4 py-3 text-sm font-medium text-[#C62828]">{err}</p>}
        {!me && !err && <Loader2 className="mx-auto mt-16 h-8 w-8 animate-spin text-zr-green" />}

        {me && (
          <div className="mt-8 grid gap-6 lg:grid-cols-5">
            <div className="space-y-6 lg:col-span-2">
              <Card title="Stato pratica" icon={Scale} testId="card-stato">
                <span data-testid="stato-badge" className="inline-flex rounded-full bg-zr-light px-3 py-1 text-xs font-bold uppercase tracking-wide text-zr-blue">
                  {humanize(me.stato)}
                </span>
                <div className="mt-4">
                  <Row label="Intestatario" value={nome} />
                  <Row label="Email" value={me.lead_email} />
                  <Row label="Tipo pratica" value={humanize(me.tipo_pratica)} />
                  <Row label="Firma completata" value={fmtDate(me.data_firma_completata) || "Non ancora"} />
                </div>
                <button onClick={downloadMiniMandato} disabled={downloading} data-testid="download-mini-mandato" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-zr-green px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#43A047] disabled:opacity-60">
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Scarica Mini-incarico
                </button>
              </Card>

              <PagamentoBonifico p={me.pagamento_bonifico} />

              <Card title="Il tuo advisor" icon={User} testId="card-advisor">
                <Row label="Nome" value={me.advisor?.nome} />
                <Row label="Email" value={me.advisor?.email} />
                <Row label="Telefono" value={me.advisor?.telefono} />
              </Card>

              {me.avvocato_difensore && Object.keys(me.avvocato_difensore).length > 0 && (
                <Card title="Avvocato difensore" icon={Scale} testId="card-avvocato">
                  <Row label="Nome" value={me.avvocato_difensore.avvocato_nome || me.avvocato_difensore.nome} />
                  <Row label="Stato" value={humanize(me.avvocato_difensore.stato)} />
                </Card>
              )}

              {me.istruttoria_svolta && (
                <Card title="Istruttoria" icon={ClipboardList} testId="card-istruttoria">
                  <Row label="Stato" value={humanize(me.istruttoria_svolta.stato)} />
                  <Row label="Pagamento" value={fmtDate(me.istruttoria_svolta.data_pagamento)} />
                  <Row label="Importo versato" value={me.istruttoria_svolta.importo_versato_eur != null ? `€ ${me.istruttoria_svolta.importo_versato_eur}` : null} />
                  <Row label="Mini-incarico firmato" value={fmtDate(me.istruttoria_svolta.mini_mandato_firmato_at)} />
                  <Row label="Consegnata" value={fmtDate(me.istruttoria_svolta.consegnato_at)} />
                  {me.istruttoria_svolta.messaggio_cliente && <p className="mt-3 rounded-xl bg-zr-light px-3 py-2 text-sm text-zr-blue">{me.istruttoria_svolta.messaggio_cliente}</p>}
                </Card>
              )}

              {me.rifiuto_emersione && (
                <Card title="Esito emersione" icon={ClipboardList} testId="card-rifiuto">
                  <Row label="Data" value={fmtDate(me.rifiuto_emersione.data)} />
                  <Row label="Rimborso parziale" value={me.rifiuto_emersione.refund_parziale_eur != null ? `€ ${me.rifiuto_emersione.refund_parziale_eur}` : null} />
                  {me.rifiuto_emersione.messaggio_cliente && <p className="mt-3 rounded-xl bg-zr-light px-3 py-2 text-sm text-zr-blue">{me.rifiuto_emersione.messaggio_cliente}</p>}
                </Card>
              )}
            </div>

            <div className="space-y-6 lg:col-span-3">
              <Card title="Fasi della pratica" testId="card-timeline">
                <Timeline items={me.timeline} />
              </Card>
              <ClienteProiezione />
              <ClienteDocumenti />
              <ClienteChat />
            </div>
          </div>
        )}
      </div>
    </main>
  );
};
